import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useXP } from '../contexts/XPContext';
import {
  getQuizFilters,
  createStudySession,
  submitStudyAnswer,
  createQuizSession,
  submitQuiz,
  getDailyChallenge,
  completeDailyChallenge,
  getDailyChallengeStreak,
  getQuizProgress,
  getQuizSettings,
  updateQuizSettings,
  resetQuizAnalytics,
  getUserSessions
} from '../api';
import DashboardLayout from '../components/layout/DashboardLayout';

function QuizPractice() {
  const { getToken } = useAuth();
  const { notifyXPGain } = useXP();

  // Tab state
  const [activeTab, setActiveTab] = useState('study');

  // Global state
  const [filters, setFilters] = useState(null);
  const [settings, setSettings] = useState(null);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Study mode state
  const [studySession, setStudySession] = useState(null);
  const [studyQuestions, setStudyQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [studyFeedback, setStudyFeedback] = useState(null);
  const [studyProgress, setStudyProgress] = useState(null);
  const [studyComplete, setStudyComplete] = useState(false);

  // Quiz mode state
  const [quizSession, setQuizSession] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [markedQuestions, setMarkedQuestions] = useState(new Set());
  const [quizComplete, setQuizComplete] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [quizTimer, setQuizTimer] = useState(null);

  // Daily challenge state
  const [dailyChallenge, setDailyChallenge] = useState(null);
  const [challengeAnswers, setChallengeAnswers] = useState({});
  const [challengeComplete, setChallengeComplete] = useState(false);
  const [challengeResults, setChallengeResults] = useState(null);

  // Progress state
  const [progress, setProgress] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);

  // Settings modal state
  const [showSettings, setShowSettings] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Filter selections
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [questionCount, setQuestionCount] = useState(10);
  const [maxAvailableQuestions, setMaxAvailableQuestions] = useState(50);
  const [isTimedQuiz, setIsTimedQuiz] = useState(false);
  const [timeLimit, setTimeLimit] = useState(30);

  // Load initial data
  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadInitialData() {
    try {
      setLoading(true);
      const token = getToken();

      const [filtersData, settingsData, streakData] = await Promise.all([
        getQuizFilters(token),
        getQuizSettings(token),
        getDailyChallengeStreak(token)
      ]);

      setFilters(filtersData);
      setSettings(settingsData);
      setStreak(streakData);

      // Load progress if analytics enabled
      if (settingsData.show_analytics) {
        const progressData = await getQuizProgress(token);
        if (!progressData.analytics_disabled) {
          setProgress(progressData);
        }
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }

  // ============================================================
  // Study Mode Functions
  // ============================================================

  async function startStudySession() {
    try {
      setLoading(true);
      const token = getToken();

      const sessionData = await createStudySession(token, {
        category: selectedCategory || null,
        difficulty: selectedDifficulty || null,
        question_count: questionCount,
        mode: 'mixed'
      });

      // Update max available based on actual returned questions
      setMaxAvailableQuestions(sessionData.questions.length);

      setStudySession(sessionData);
      setStudyQuestions(sessionData.questions);
      setCurrentQuestionIndex(0);
      setSelectedAnswer('');
      setStudyFeedback(null);
      setStudyComplete(false);

      setLoading(false);
    } catch (err) {
      // Check if error is about insufficient questions
      if (err.message && err.message.includes('questions available')) {
        const match = err.message.match(/only (\d+) questions available/i);
        if (match) {
          const available = parseInt(match[1]);
          setMaxAvailableQuestions(available);
          alert(`Only ${available} questions available with these filters. Please select ${available} or fewer questions.`);
        } else {
          alert('Not enough questions available with these filters. Try selecting fewer questions or different filters.');
        }
      } else {
        alert('Failed to start study session: ' + err.message);
      }
      setLoading(false);
    }
  }

  async function submitCurrentStudyAnswer() {
    if (!selectedAnswer) {
      alert('Please select an answer');
      return;
    }

    try {
      setLoading(true);
      const token = getToken();
      const currentQuestion = studyQuestions[currentQuestionIndex];

      const result = await submitStudyAnswer(token, {
        session_id: studySession.id,
        question_id: currentQuestion.id,
        user_answer: selectedAnswer,
        time_spent: null
      });

      setStudyFeedback(result);
      setStudyProgress(result.session_progress);
      setLoading(false);
    } catch (err) {
      alert('Failed to submit answer: ' + err.message);
      setLoading(false);
    }
  }

  function nextStudyQuestion() {
    if (currentQuestionIndex < studyQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer('');
      setStudyFeedback(null);
    } else {
      // Session complete
      setStudyComplete(true);
    }
  }

  function resetStudyMode() {
    setStudySession(null);
    setStudyQuestions([]);
    setCurrentQuestionIndex(0);
    setSelectedAnswer('');
    setStudyFeedback(null);
    setStudyProgress(null);
    setStudyComplete(false);
  }

  // ============================================================
  // Quiz Mode Functions
  // ============================================================

  async function startQuizSession() {
    try {
      setLoading(true);
      const token = getToken();

      const sessionData = await createQuizSession(token, {
        category: selectedCategory || null,
        difficulty: selectedDifficulty || null,
        question_count: questionCount,
        is_timed: isTimedQuiz,
        time_limit: isTimedQuiz ? timeLimit : null
      });

      // Update max available based on actual returned questions
      setMaxAvailableQuestions(sessionData.questions.length);

      setQuizSession(sessionData);
      setQuizQuestions(sessionData.questions);
      setQuizAnswers({});
      setMarkedQuestions(new Set());
      setQuizComplete(false);

      // Start timer if timed
      if (isTimedQuiz && timeLimit) {
        setQuizTimer(timeLimit * 60); // Convert to seconds
      }

      setLoading(false);
    } catch (err) {
      // Check if error is about insufficient questions
      if (err.message && err.message.includes('questions available')) {
        const match = err.message.match(/only (\d+) questions available/i);
        if (match) {
          const available = parseInt(match[1]);
          setMaxAvailableQuestions(available);
          alert(`Only ${available} questions available with these filters. Please select ${available} or fewer questions.`);
        } else {
          alert('Not enough questions available with these filters. Try selecting fewer questions or different filters.');
        }
      } else {
        alert('Failed to start quiz: ' + err.message);
      }
      setLoading(false);
    }
  }

  // Timer countdown
  useEffect(() => {
    if (quizTimer !== null && quizTimer > 0 && !quizComplete) {
      const interval = setInterval(() => {
        setQuizTimer(prev => {
          if (prev <= 1) {
            // Time's up, auto-submit
            handleSubmitQuiz();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizTimer, quizComplete]);

  function updateQuizAnswer(questionId, answer) {
    setQuizAnswers({
      ...quizAnswers,
      [questionId]: answer
    });
  }

  function toggleMarkForReview(questionId) {
    const newMarked = new Set(markedQuestions);
    if (newMarked.has(questionId)) {
      newMarked.delete(questionId);
    } else {
      newMarked.add(questionId);
    }
    setMarkedQuestions(newMarked);
  }

  async function handleSubmitQuiz() {
    try {
      setLoading(true);
      const token = getToken();

      // Build answers array
      const answers = quizQuestions.map(q => ({
        question_id: q.id,
        user_answer: quizAnswers[q.id] || '',
        time_spent: 0,
        marked_for_review: markedQuestions.has(q.id)
      }));

      const results = await submitQuiz(token, {
        session_id: quizSession.id,
        answers: answers
      });

      setQuizResults(results);
      setQuizComplete(true);

      // Notify XP gain if XP was awarded
      if (results.xp_awarded) {
        notifyXPGain(results.xp_awarded);
      }

      setLoading(false);
    } catch (err) {
      alert('Failed to submit quiz: ' + err.message);
      setLoading(false);
    }
  }

  function resetQuizMode() {
    setQuizSession(null);
    setQuizQuestions([]);
    setQuizAnswers({});
    setMarkedQuestions(new Set());
    setQuizComplete(false);
    setQuizResults(null);
    setQuizTimer(null);
  }

  // ============================================================
  // Daily Challenge Functions
  // ============================================================

  async function loadDailyChallenge() {
    try {
      setLoading(true);
      const token = getToken();

      const challengeData = await getDailyChallenge(token);
      setDailyChallenge(challengeData);
      setChallengeAnswers({});
      setChallengeComplete(false);

      setLoading(false);
    } catch (err) {
      alert('Failed to load daily challenge: ' + err.message);
      setLoading(false);
    }
  }

  // Load challenge when tab switches
  useEffect(() => {
    if (activeTab === 'daily' && !dailyChallenge) {
      loadDailyChallenge();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  function updateChallengeAnswer(questionId, answer) {
    setChallengeAnswers({
      ...challengeAnswers,
      [questionId]: answer
    });
  }

  async function submitDailyChallenge() {
    try {
      setLoading(true);
      const token = getToken();

      const answers = dailyChallenge.questions.map(q => ({
        question_id: q.id,
        user_answer: challengeAnswers[q.id] || '',
        time_spent: 0
      }));

      const results = await completeDailyChallenge(token, {
        challenge_id: dailyChallenge.challenge_id,
        answers: answers
      });

      setChallengeResults(results);
      setChallengeComplete(true);

      // Notify XP gain if XP was awarded
      if (results.xp_awarded) {
        notifyXPGain(results.xp_awarded);
      }

      // Reload streak
      const streakData = await getDailyChallengeStreak(token);
      setStreak(streakData);

      setLoading(false);
    } catch (err) {
      alert('Failed to submit challenge: ' + err.message);
      setLoading(false);
    }
  }

  function resetDailyChallenge() {
    setDailyChallenge(null);
    setChallengeAnswers({});
    setChallengeComplete(false);
    setChallengeResults(null);
  }

  // ============================================================
  // Progress Functions
  // ============================================================

  async function loadProgress() {
    try {
      setLoading(true);
      const token = getToken();

      const progressData = await getQuizProgress(token);
      if (!progressData.analytics_disabled) {
        setProgress(progressData);

        const sessionsData = await getUserSessions(token, null, 10);
        setRecentSessions(sessionsData.sessions || []);
      }

      setLoading(false);
    } catch (err) {
      alert('Failed to load progress: ' + err.message);
      setLoading(false);
    }
  }

  useEffect(() => {
    if (activeTab === 'progress' && settings?.show_analytics) {
      loadProgress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, settings]);

  // ============================================================
  // Settings Functions
  // ============================================================

  async function toggleAnalytics() {
    try {
      setLoading(true);
      const token = getToken();

      const updated = await updateQuizSettings(token, {
        show_analytics: !settings.show_analytics
      });

      setSettings(updated);
      setLoading(false);
    } catch (err) {
      alert('Failed to update settings: ' + err.message);
      setLoading(false);
    }
  }

  async function handleResetAnalytics() {
    try {
      setLoading(true);
      const token = getToken();

      await resetQuizAnalytics(token);

      setProgress(null);
      setRecentSessions([]);
      setShowResetConfirm(false);
      setShowSettings(false);

      alert('✅ Analytics reset successfully!');
      setLoading(false);
    } catch (err) {
      alert('Failed to reset analytics: ' + err.message);
      setLoading(false);
    }
  }

  // ============================================================
  // Render Functions
  // ============================================================


  function renderStreakBanner() {
    if (!streak) return null;

    return (
      <div style={styles.streakBanner}>
        🔥 {streak.current_streak} day streak • {streak.total_completed} challenges completed
      </div>
    );
  }

  function renderTabs() {
    const tabs = [
      { id: 'study', label: '📚 Study', icon: '📚' },
      { id: 'quiz', label: '✍️ Quiz', icon: '✍️' },
      { id: 'daily', label: '⭐ Daily Challenge', icon: '⭐' },
      { id: 'progress', label: '📊 Progress', icon: '📊' }
    ];

    return (
      <div style={styles.tabContainer}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.activeTab : {})
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
    );
  }

  function renderStudyMode() {
    if (studyComplete && studyProgress) {
      return (
        <div style={styles.card}>
          <h2>Study Session Complete! 🎉</h2>
          <div style={styles.scoreCard}>
            <div style={styles.score}>
              {studyProgress.score.toFixed(0)}%
            </div>
            <div style={styles.scoreDetails}>
              {studyProgress.correct} / {studyProgress.total} correct
            </div>
          </div>
          <button onClick={resetStudyMode} style={styles.button}>
            Start New Session
          </button>
        </div>
      );
    }

    if (!studySession) {
      return (
        <div style={styles.card}>
          <h2>Study Mode</h2>
          <p style={styles.description}>
            Practice questions one at a time with immediate feedback
          </p>

          <div style={styles.filterSection}>
            <div style={styles.filterRow}>
              <label style={styles.label}>Category:</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={styles.select}
              >
                <option value="">All Categories</option>
                {filters?.categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div style={styles.filterRow}>
              <label style={styles.label}>Difficulty:</label>
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                style={styles.select}
              >
                <option value="">All Difficulties</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>

            <div style={styles.filterRow}>
              <label style={styles.label}>Questions (max {maxAvailableQuestions}):</label>
              <input
                type="number"
                value={questionCount}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (val > maxAvailableQuestions) {
                    setQuestionCount(maxAvailableQuestions);
                  } else if (val < 1) {
                    setQuestionCount(1);
                  } else {
                    setQuestionCount(val);
                  }
                }}
                min="1"
                max={maxAvailableQuestions}
                style={styles.input}
              />
            </div>

            <button
              onClick={startStudySession}
              disabled={loading}
              style={styles.button}
            >
              {loading ? 'Loading...' : 'Start Study Session'}
            </button>
          </div>
        </div>
      );
    }

    // Active study session
    const currentQuestion = studyQuestions[currentQuestionIndex];

    return (
      <div>
        {/* Progress bar */}
        <div style={styles.progressBar}>
          <div style={styles.progressText}>
            Question {currentQuestionIndex + 1} / {studyQuestions.length}
            {studyProgress && (
              <span style={styles.scoreText}>
                • Score: {studyProgress.correct} / {studyProgress.answered} ({studyProgress.score.toFixed(0)}%)
              </span>
            )}
          </div>
          <div style={styles.progressBarOuter}>
            <div
              style={{
                ...styles.progressBarInner,
                width: `${((currentQuestionIndex + 1) / studyQuestions.length) * 100}%`
              }}
            />
          </div>
        </div>

        {/* Question card */}
        <div style={styles.card}>
          <div style={styles.questionHeader}>
            <span style={styles.badge}>{currentQuestion.difficulty}</span>
            <span style={styles.category}>{currentQuestion.category}</span>
          </div>

          <h3 style={styles.questionText}>{currentQuestion.question}</h3>

          <div style={styles.choicesContainer}>
            {currentQuestion.choices.map((choice, idx) => (
              <button
                key={idx}
                className="quiz-choice-button"
                onClick={(e) => {
                  if (!studyFeedback) {
                    setSelectedAnswer(choice);
                    e.currentTarget.blur();
                  }
                }}
                onMouseDown={(e) => e.preventDefault()}
                disabled={!!studyFeedback}
                style={{
                  ...styles.choiceButton,
                  ...(selectedAnswer === choice ? styles.choiceButtonSelected : {}),
                  ...(studyFeedback && choice.trim() === studyFeedback.correct_answer.trim()
                    ? styles.choiceButtonCorrect
                    : {}),
                  ...(studyFeedback && selectedAnswer === choice && !studyFeedback.is_correct
                    ? styles.choiceButtonWrong
                    : {})
                }}
              >
                {choice}
              </button>
            ))}
          </div>

          {!studyFeedback && (
            <button
              onClick={submitCurrentStudyAnswer}
              disabled={!selectedAnswer || loading}
              style={styles.button}
            >
              {loading ? 'Submitting...' : 'Submit Answer'}
            </button>
          )}

          {studyFeedback && (
            <div style={{
              ...styles.feedbackBox,
              ...(studyFeedback.is_correct ? styles.feedbackCorrect : styles.feedbackWrong)
            }}>
              <h4>{studyFeedback.is_correct ? '✅ Correct!' : '❌ Incorrect'}</h4>
              {!studyFeedback.is_correct && (
                <p>Correct answer: {studyFeedback.correct_answer}</p>
              )}
              {studyFeedback.explanation && (
                <p style={styles.explanation}>{studyFeedback.explanation}</p>
              )}
              <button onClick={nextStudyQuestion} style={styles.button}>
                {currentQuestionIndex < studyQuestions.length - 1
                  ? 'Next Question →'
                  : 'Finish Session'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderQuizMode() {
    if (quizComplete && quizResults) {
      return (
        <div style={styles.card}>
          <h2>Quiz Complete! 🎉</h2>
          <div style={styles.scoreCard}>
            <div style={styles.score}>{quizResults.score.toFixed(0)}%</div>
            <div style={styles.scoreDetails}>
              {quizResults.correct_answers} / {quizResults.total_questions} correct
            </div>
            <div style={styles.scoreDetails}>
              Time: {Math.floor(quizResults.time_spent / 60)}m {quizResults.time_spent % 60}s
            </div>
          </div>

          <h3>Review Answers:</h3>
          {quizResults.results.map((result, idx) => (
            <div
              key={idx}
              style={{
                ...styles.resultItem,
                borderLeft: `4px solid ${result.is_correct ? '#22c55e' : '#ef4444'}`
              }}
            >
              <div style={styles.resultHeader}>
                <span>{result.is_correct ? '✅' : '❌'}</span>
                <span style={styles.questionSmall}>{result.question}</span>
              </div>
              <div style={styles.resultDetails}>
                <div>Your answer: {result.user_answer}</div>
                {!result.is_correct && (
                  <div>Correct answer: {result.correct_answer}</div>
                )}
                {result.explanation && (
                  <div style={styles.explanationSmall}>{result.explanation}</div>
                )}
              </div>
            </div>
          ))}

          <button onClick={resetQuizMode} style={styles.button}>
            Start New Quiz
          </button>
        </div>
      );
    }

    if (!quizSession) {
      return (
        <div style={styles.card}>
          <h2>Quiz Mode</h2>
          <p style={styles.description}>
            Answer all questions, then submit for grading
          </p>

          <div style={styles.filterSection}>
            <div style={styles.filterRow}>
              <label style={styles.label}>Category:</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={styles.select}
              >
                <option value="">All Categories</option>
                {filters?.categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div style={styles.filterRow}>
              <label style={styles.label}>Difficulty:</label>
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                style={styles.select}
              >
                <option value="">All Difficulties</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>

            <div style={styles.filterRow}>
              <label style={styles.label}>Questions (max {maxAvailableQuestions}):</label>
              <input
                type="number"
                value={questionCount}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (val > maxAvailableQuestions) {
                    setQuestionCount(maxAvailableQuestions);
                  } else if (val < 1) {
                    setQuestionCount(1);
                  } else {
                    setQuestionCount(val);
                  }
                }}
                min="5"
                max={maxAvailableQuestions}
                style={styles.input}
              />
            </div>

            <div style={styles.filterRow}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={isTimedQuiz}
                  onChange={(e) => setIsTimedQuiz(e.target.checked)}
                  style={styles.checkbox}
                />
                Timed Quiz
              </label>
            </div>

            {isTimedQuiz && (
              <div style={styles.filterRow}>
                <label style={styles.label}>Time Limit (minutes):</label>
                <input
                  type="number"
                  value={timeLimit}
                  onChange={(e) => setTimeLimit(parseInt(e.target.value))}
                  min="5"
                  max="120"
                  style={styles.input}
                />
              </div>
            )}

            <button
              onClick={startQuizSession}
              disabled={loading}
              style={styles.button}
            >
              {loading ? 'Loading...' : 'Start Quiz'}
            </button>
          </div>
        </div>
      );
    }

    // Active quiz
    return (
      <div>
        {/* Timer */}
        {quizTimer !== null && (
          <div style={styles.timerBox}>
            ⏱️ Time Remaining: {Math.floor(quizTimer / 60)}:{String(quizTimer % 60).padStart(2, '0')}
          </div>
        )}

        {/* Questions */}
        <div style={styles.quizGrid}>
          {quizQuestions.map((question, idx) => (
            <div key={question.id} style={styles.card}>
              <div style={styles.questionHeader}>
                <span style={styles.questionNumber}>Question {idx + 1}</span>
                <span style={styles.badge}>{question.difficulty}</span>
                <span style={styles.category}>{question.category}</span>
              </div>

              <h4 style={styles.questionText}>{question.question}</h4>

              <div style={styles.choicesContainer}>
                {question.choices.map((choice, cIdx) => (
                  <button
                    key={cIdx}
                    className="quiz-choice-button"
                    onClick={(e) => {
                      updateQuizAnswer(question.id, choice);
                      e.currentTarget.blur();
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    style={{
                      ...styles.choiceButton,
                      ...(quizAnswers[question.id] === choice ? styles.choiceButtonSelected : {})
                    }}
                  >
                    {choice}
                  </button>
                ))}
              </div>

              <button
                onClick={() => toggleMarkForReview(question.id)}
                style={{
                  ...styles.markButton,
                  ...(markedQuestions.has(question.id) ? styles.markButtonActive : {})
                }}
              >
                {markedQuestions.has(question.id) ? '🚩 Marked' : '🏳️ Mark for Review'}
              </button>
            </div>
          ))}
        </div>

        {/* Submit button */}
        <div style={styles.submitSection}>
          <button
            onClick={handleSubmitQuiz}
            disabled={loading}
            style={styles.submitButton}
          >
            {loading ? 'Submitting...' : 'Submit Quiz'}
          </button>
          <div style={styles.submitHint}>
            Answered: {Object.keys(quizAnswers).length} / {quizQuestions.length}
          </div>
        </div>
      </div>
    );
  }

  function renderDailyChallenge() {
    if (challengeComplete && challengeResults) {
      return (
        <div style={styles.card}>
          <h2>{challengeResults.is_first_completion ? '🎉 Challenge Complete!' : '✅ Practice Complete'}</h2>

          {!challengeResults.is_first_completion && (
            <div style={styles.practiceNotice}>
              This is a practice round. You already completed today's challenge!
            </div>
          )}

          <div style={styles.scoreCard}>
            <div style={styles.score}>{challengeResults.score.toFixed(0)}%</div>
            <div style={styles.scoreDetails}>
              {challengeResults.correct_answers} / 5 correct
            </div>
          </div>

          {challengeResults.streak_updated && (
            <div style={styles.streakUpdate}>
              🔥 Streak Updated: {streak?.current_streak || 0} days!
            </div>
          )}

          <button onClick={resetDailyChallenge} style={styles.button}>
            Try Another Practice Round
          </button>
        </div>
      );
    }

    if (!dailyChallenge) {
      return (
        <div style={styles.card}>
          <h2>Daily Challenge</h2>
          <div style={styles.challengeInfo}>
            Complete 5 random questions daily to build your streak!
          </div>
          {loading && <p>Loading challenge...</p>}
        </div>
      );
    }

    return (
      <div>
        <div style={styles.challengeHeader}>
          <h2>Today's Challenge</h2>
          <div style={styles.challengeDate}>
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric'
            })}
          </div>
          {dailyChallenge.user_has_completed && !dailyChallenge.is_first_attempt && (
            <div style={styles.practiceNotice}>
              Practice Round - Already completed today
            </div>
          )}
        </div>

        {dailyChallenge.questions.map((question, idx) => (
          <div key={question.id} style={styles.card}>
            <div style={styles.questionHeader}>
              <span style={styles.questionNumber}>Question {idx + 1}/5</span>
              <span style={styles.badge}>{question.difficulty}</span>
              <span style={styles.category}>{question.category}</span>
            </div>

            <h4 style={styles.questionText}>{question.question}</h4>

            <div style={styles.choicesContainer}>
              {question.choices.map((choice, cIdx) => (
                <button
                  key={cIdx}
                  className="quiz-choice-button"
                  onClick={(e) => {
                    updateChallengeAnswer(question.id, choice);
                    e.currentTarget.blur();
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  style={{
                    ...styles.choiceButton,
                    ...(challengeAnswers[question.id] === choice ? styles.choiceButtonSelected : {})
                  }}
                >
                  {choice}
                </button>
              ))}
            </div>
          </div>
        ))}

        <div style={styles.submitSection}>
          <button
            onClick={submitDailyChallenge}
            disabled={loading || Object.keys(challengeAnswers).length !== 5}
            style={styles.submitButton}
          >
            {loading ? 'Submitting...' : 'Submit Challenge'}
          </button>
          <div style={styles.submitHint}>
            Answered: {Object.keys(challengeAnswers).length} / 5
          </div>
        </div>
      </div>
    );
  }

  function renderProgress() {
    if (!settings?.show_analytics) {
      return (
        <div style={styles.card}>
          <h2>Analytics Hidden</h2>
          <p style={styles.description}>
            Analytics are currently hidden. Enable them in settings to track your progress.
          </p>
          <button onClick={() => setShowSettings(true)} style={styles.button}>
            Open Settings
          </button>
        </div>
      );
    }

    if (!progress) {
      return (
        <div style={styles.card}>
          <h2>Progress Dashboard</h2>
          {loading ? <p>Loading...</p> : <p>No progress data available yet. Start practicing to see your stats!</p>}
        </div>
      );
    }

    return (
      <div>
        <h2>Your Progress</h2>

        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{progress.total_sessions}</div>
            <div style={styles.statLabel}>Sessions Completed</div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statValue}>{progress.total_questions_attempted}</div>
            <div style={styles.statLabel}>Questions Attempted</div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statValue}>{progress.overall_accuracy.toFixed(1)}%</div>
            <div style={styles.statLabel}>Overall Accuracy</div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statValue}>{progress.average_score.toFixed(1)}%</div>
            <div style={styles.statLabel}>Average Score</div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statValue}>{progress.daily_challenge_streak}</div>
            <div style={styles.statLabel}>Current Streak</div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statValue}>{progress.daily_challenges_completed}</div>
            <div style={styles.statLabel}>Challenges Completed</div>
          </div>
        </div>

        {recentSessions.length > 0 && (
          <div style={styles.card}>
            <h3>Recent Sessions</h3>
            {recentSessions.map(session => (
              <div key={session.id} style={styles.sessionItem}>
                <div style={styles.sessionType}>
                  {session.session_type === 'study' ? '📚' : session.session_type === 'quiz' ? '✍️' : '⭐'}{' '}
                  {session.session_type}
                </div>
                <div style={styles.sessionDetails}>
                  {session.category && <span>{session.category}</span>}
                  {session.difficulty && <span>• {session.difficulty}</span>}
                  <span>• {session.score?.toFixed(0)}%</span>
                </div>
                <div style={styles.sessionDate}>
                  {new Date(session.started_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  function renderSettingsModal() {
    if (!showSettings) return null;

    return (
      <div style={styles.modalOverlay} onClick={() => setShowSettings(false)}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.modalHeader}>
            <h2>Quiz Settings</h2>
            <button onClick={() => setShowSettings(false)} style={styles.closeButton}>
              ✕
            </button>
          </div>

          <div style={styles.modalContent}>
            <div style={styles.settingItem}>
              <label style={styles.settingLabel}>
                <input
                  type="checkbox"
                  checked={settings?.show_analytics || false}
                  onChange={toggleAnalytics}
                  disabled={loading}
                  style={styles.checkbox}
                />
                Show Quiz Analytics
              </label>
              <p style={styles.settingHint}>
                Track your progress, accuracy, and improvement over time
              </p>
            </div>

            <div style={styles.divider} />

            <div style={styles.settingItem}>
              <h3 style={styles.dangerHeading}>Danger Zone</h3>
              <button
                onClick={() => setShowResetConfirm(true)}
                disabled={loading}
                style={styles.dangerButton}
              >
                Reset All Analytics
              </button>
              <p style={styles.settingHint}>
                Permanently delete all session history and progress data. Streaks will be preserved.
              </p>
            </div>
          </div>

          {/* Reset Confirmation */}
          {showResetConfirm && (
            <div style={styles.confirmBox}>
              <h3>⚠️ Reset Quiz Progress?</h3>
              <p>This will permanently delete:</p>
              <ul style={styles.confirmList}>
                <li>All quiz session history</li>
                <li>All question attempts</li>
                <li>All progress statistics</li>
              </ul>
              <p style={styles.confirmNote}>
                Daily challenge streaks will be preserved.<br />
                This action cannot be undone.
              </p>
              <div style={styles.confirmButtons}>
                <button
                  onClick={() => setShowResetConfirm(false)}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetAnalytics}
                  disabled={loading}
                  style={styles.confirmButton}
                >
                  {loading ? 'Resetting...' : 'Yes, Reset Everything'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main render
  if (loading && !filters) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-base)', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: '18px', color: '#888' }}>Loading quiz practice...</div>
      </div>
    );
  }

  return (
    <DashboardLayout
      title="Quiz Practice"
      subtitle="Study, Quiz, and Daily challenge modes"
    >
      <style>{`
        * {
          -webkit-tap-highlight-color: transparent !important;
        }
        button,
        button:focus,
        button:active,
        button:focus-visible,
        .quiz-choice-button,
        .quiz-choice-button:focus,
        .quiz-choice-button:focus-visible,
        .quiz-choice-button:active {
          outline: 0 !important;
          outline-width: 0 !important;
          outline-style: none !important;
          outline-color: transparent !important;
          outline-offset: 0 !important;
          box-shadow: none !important;
          -webkit-box-shadow: none !important;
          -moz-box-shadow: none !important;
        }
      `}</style>

      {/* Settings Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-lg)' }}>
        <button
          onClick={() => setShowSettings(true)}
          style={{
            padding: 'var(--space-sm) var(--space-lg)',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-light)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-primary)',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: 500,
            transition: 'all var(--duration-fast)'
          }}
          onMouseOver={(e) => {
            e.target.style.background = 'var(--bg-elevated)';
            e.target.style.borderColor = 'var(--border-medium)';
          }}
          onMouseOut={(e) => {
            e.target.style.background = 'var(--bg-surface)';
            e.target.style.borderColor = 'var(--border-light)';
          }}
        >
          Settings
        </button>
      </div>
        {renderStreakBanner()}
        {renderTabs()}

        {error && (
          <div style={styles.errorBox}>
            ❌ {error}
          </div>
        )}

        <div style={styles.content}>
          {activeTab === 'study' && renderStudyMode()}
          {activeTab === 'quiz' && renderQuizMode()}
          {activeTab === 'daily' && renderDailyChallenge()}
          {activeTab === 'progress' && renderProgress()}
        </div>

        {renderSettingsModal()}
    </DashboardLayout>
  );
}

// ============================================================
// Styles
// ============================================================

const styles = {
  streakBanner: {
    padding: '15px',
    backgroundColor: 'var(--bg-elevated)',
    border: '1px solid var(--indigo-500)',
    borderRadius: '8px',
    textAlign: 'center',
    marginBottom: '20px',
    fontSize: '18px',
  },
  tabContainer: {
    display: 'flex',
    gap: '10px',
    marginBottom: '30px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  tab: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    color: '#888',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'all 0.2s',
  },
  activeTab: {
    color: 'var(--indigo-500)',
    borderBottomColor: 'var(--indigo-500)',
  },
  content: {
    minHeight: '400px',
  },
  card: {
    backgroundColor: 'var(--bg-elevated)',
    border: '1px solid var(--border-light)',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '20px',
  },
  description: {
    color: '#888',
    fontSize: '16px',
    marginBottom: '20px',
  },
  filterSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  label: {
    minWidth: '150px',
    color: 'var(--text-primary)',
  },
  select: {
    flex: 1,
    padding: '10px',
    backgroundColor: 'var(--bg-base)',
    border: '1px solid var(--border-light)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '14px',
  },
  input: {
    flex: 1,
    padding: '10px',
    backgroundColor: 'var(--bg-base)',
    border: '1px solid var(--border-light)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '14px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: 'var(--text-primary)',
    cursor: 'pointer',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  button: {
    padding: '12px 24px',
    background: 'var(--gradient-primary)',
    border: 'none',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    marginTop: '10px',
  },
  progressBar: {
    marginBottom: '20px',
  },
  progressText: {
    fontSize: '16px',
    marginBottom: '8px',
    color: 'var(--text-primary)',
  },
  scoreText: {
    color: 'var(--indigo-500)',
    marginLeft: '10px',
  },
  progressBarOuter: {
    height: '8px',
    backgroundColor: 'var(--bg-elevated)',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressBarInner: {
    height: '100%',
    background: 'var(--gradient-primary)',
    transition: 'width 0.3s',
  },
  questionHeader: {
    display: 'flex',
    gap: '10px',
    marginBottom: '15px',
    flexWrap: 'wrap',
  },
  badge: {
    padding: '4px 12px',
    backgroundColor: 'var(--indigo-500)',
    borderRadius: '12px',
    fontSize: '12px',
    textTransform: 'uppercase',
    fontWeight: '600',
  },
  category: {
    padding: '4px 12px',
    backgroundColor: 'var(--bg-elevated)',
    border: '1px solid var(--border-light)',
    borderRadius: '12px',
    fontSize: '12px',
  },
  questionNumber: {
    fontSize: '14px',
    color: '#888',
  },
  questionText: {
    fontSize: '18px',
    lineHeight: '1.6',
    marginBottom: '20px',
    color: 'var(--text-primary)',
  },
  choicesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '20px',
  },
  choiceButton: {
    padding: '15px',
    backgroundColor: 'var(--bg-base)',
    border: '2px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    color: 'var(--text-primary)',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '15px',
    outline: 'none',
    boxShadow: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  choiceButtonSelected: {
    borderColor: 'var(--indigo-500)',
    backgroundColor: 'rgba(147, 51, 234, 0.1)',
    outline: 'none',
    boxShadow: 'none',
  },
  choiceButtonCorrect: {
    borderColor: '#22c55e',
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    outline: 'none',
    boxShadow: 'none',
  },
  choiceButtonWrong: {
    borderColor: '#ef4444',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    outline: 'none',
    boxShadow: 'none',
  },
  feedbackBox: {
    padding: '20px',
    borderRadius: '8px',
    marginTop: '20px',
  },
  feedbackCorrect: {
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    border: '2px solid #22c55e',
  },
  feedbackWrong: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '2px solid #ef4444',
  },
  explanation: {
    marginTop: '10px',
    color: '#ccc',
    fontSize: '14px',
    lineHeight: '1.6',
  },
  scoreCard: {
    textAlign: 'center',
    padding: '30px',
    marginBottom: '20px',
  },
  score: {
    fontSize: '72px',
    fontWeight: 'bold',
    background: 'var(--gradient-primary)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '10px',
  },
  scoreDetails: {
    fontSize: '18px',
    color: '#888',
    marginTop: '5px',
  },
  quizGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '20px',
  },
  timerBox: {
    padding: '15px',
    backgroundColor: 'var(--bg-elevated)',
    borderRadius: '8px',
    textAlign: 'center',
    marginBottom: '20px',
    fontSize: '20px',
    fontWeight: 'bold',
    color: 'var(--indigo-500)',
  },
  markButton: {
    padding: '8px 16px',
    backgroundColor: 'var(--bg-base)',
    border: '1px solid var(--border-light)',
    borderRadius: '6px',
    color: '#888',
    cursor: 'pointer',
    fontSize: '14px',
    marginTop: '10px',
  },
  markButtonActive: {
    borderColor: 'var(--indigo-500)',
    color: 'var(--indigo-500)',
  },
  submitSection: {
    textAlign: 'center',
    marginTop: '30px',
  },
  submitButton: {
    padding: '16px 40px',
    background: 'var(--gradient-primary)',
    border: 'none',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitHint: {
    marginTop: '10px',
    color: '#888',
    fontSize: '14px',
  },
  resultItem: {
    padding: '15px',
    backgroundColor: 'var(--bg-base)',
    borderRadius: '8px',
    marginBottom: '10px',
  },
  resultHeader: {
    display: 'flex',
    gap: '10px',
    marginBottom: '10px',
    alignItems: 'flex-start',
  },
  questionSmall: {
    flex: 1,
    fontSize: '14px',
  },
  resultDetails: {
    fontSize: '13px',
    color: '#888',
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  explanationSmall: {
    marginTop: '5px',
    color: '#999',
    fontSize: '12px',
    fontStyle: 'italic',
  },
  challengeHeader: {
    marginBottom: '20px',
  },
  challengeDate: {
    fontSize: '14px',
    color: '#888',
    marginBottom: '10px',
  },
  challengeInfo: {
    padding: '15px',
    backgroundColor: 'var(--bg-base)',
    borderRadius: '8px',
    color: '#888',
    textAlign: 'center',
  },
  practiceNotice: {
    padding: '10px',
    backgroundColor: 'rgba(147, 51, 234, 0.1)',
    border: '1px solid var(--indigo-500)',
    borderRadius: '6px',
    color: 'var(--indigo-500)',
    textAlign: 'center',
    fontSize: '14px',
    marginTop: '10px',
  },
  streakUpdate: {
    padding: '15px',
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    border: '1px solid #22c55e',
    borderRadius: '8px',
    color: '#22c55e',
    textAlign: 'center',
    marginTop: '15px',
    fontSize: '18px',
    fontWeight: '600',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    marginBottom: '30px',
  },
  statCard: {
    padding: '20px',
    backgroundColor: 'var(--bg-elevated)',
    border: '1px solid var(--border-light)',
    borderRadius: '8px',
    textAlign: 'center',
  },
  statValue: {
    fontSize: '36px',
    fontWeight: 'bold',
    color: 'var(--indigo-500)',
    marginBottom: '5px',
  },
  statLabel: {
    fontSize: '14px',
    color: '#888',
  },
  sessionItem: {
    padding: '15px',
    backgroundColor: 'var(--bg-base)',
    borderRadius: '8px',
    marginBottom: '10px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sessionType: {
    fontSize: '16px',
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  sessionDetails: {
    flex: 1,
    marginLeft: '15px',
    color: '#888',
    fontSize: '14px',
  },
  sessionDate: {
    fontSize: '12px',
    color: 'var(--text-muted)',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 'var(--sidebar-width)',
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'var(--bg-elevated)',
    borderRadius: '12px',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
  },
  modalHeader: {
    padding: '20px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#888',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '5px',
  },
  modalContent: {
    padding: '20px',
  },
  settingItem: {
    marginBottom: '20px',
  },
  settingLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '16px',
    cursor: 'pointer',
  },
  settingHint: {
    fontSize: '13px',
    color: '#888',
    marginTop: '5px',
    marginLeft: '28px',
  },
  divider: {
    height: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    margin: '20px 0',
  },
  dangerHeading: {
    color: '#ef4444',
    fontSize: '16px',
    marginBottom: '10px',
  },
  dangerButton: {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    border: '2px solid #ef4444',
    borderRadius: '8px',
    color: '#ef4444',
    cursor: 'pointer',
    fontSize: '14px',
  },
  confirmBox: {
    padding: '20px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },
  confirmList: {
    marginLeft: '20px',
    marginBottom: '15px',
    color: '#888',
  },
  confirmNote: {
    fontSize: '13px',
    color: '#888',
    marginBottom: '20px',
  },
  confirmButtons: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: 'var(--bg-elevated)',
    border: '1px solid var(--border-light)',
    borderRadius: '8px',
    color: 'var(--text-primary)',
    cursor: 'pointer',
  },
  confirmButton: {
    padding: '10px 20px',
    backgroundColor: '#ef4444',
    border: 'none',
    borderRadius: '8px',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    fontWeight: '600',
  },
  errorBox: {
    padding: '15px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid #ef4444',
    borderRadius: '8px',
    color: '#ef4444',
    marginBottom: '20px',
  },
};

export default QuizPractice;
